import type { LibrarySketch } from './types';

export const interactiveSketches: LibrarySketch[] = [
  {
    id: 'interactive-mouse-particles',
    name: 'Mouse Particle Emitter',
    description: 'Particles continuously spawn at the mouse position and drift outward with physics, fading over their lifetime.',
    category: 'interactive',
    tags: ['mouse', 'particles', 'emit', 'interactive', 'physics'],
    runtimeMode: 'p5',
    inputTypes: [],
    params: [
      { name: 'emitRate', type: 'int', min: 1, max: 20, default: 5, label: 'Emit Rate' },
      { name: 'lifetime', type: 'float', min: 0.5, max: 5.0, default: 2.0, label: 'Lifetime (s)' },
      { name: 'gravity', type: 'float', min: -0.2, max: 0.2, default: 0.02, label: 'Gravity' },
      { name: 'size', type: 'float', min: 2, max: 20, default: 8, label: 'Size' },
    ],
    code: `// @param int emitRate 1 20 5
// @param float lifetime 0.5 5.0 2.0
// @param float gravity -0.2 0.2 0.02
// @param float size 2 20 8

let particles = [];
function setup() {
  createCanvas(genie.width, genie.height);
  colorMode(HSB, 360, 100, 100, 100);
}
function draw() {
  background(0, 20);
  const er = genie.params.emitRate ?? 5;
  const lt = (genie.params.lifetime ?? 2.0) * 60;
  const gv = genie.params.gravity ?? 0.02;
  const sz = genie.params.size ?? 8;
  const mx = mouseX || width / 2;
  const my = mouseY || height / 2;
  for (let i = 0; i < er; i++) {
    const angle = random(TWO_PI);
    const speed = random(0.5, 3);
    particles.push({
      x: mx, y: my,
      vx: cos(angle) * speed, vy: sin(angle) * speed,
      life: lt, maxLife: lt,
      hue: (frameCount * 2 + random(30)) % 360
    });
  }
  noStroke();
  for (let i = particles.length - 1; i >= 0; i--) {
    const p = particles[i];
    p.x += p.vx; p.y += p.vy;
    p.vy += gv;
    p.life--;
    const t = p.life / p.maxLife;
    fill(p.hue, 80, 100, t * 80);
    circle(p.x, p.y, sz * t);
    if (p.life <= 0) particles.splice(i, 1);
  }
  if (particles.length > 3000) particles.splice(0, particles.length - 3000);
}`,
  },

  {
    id: 'interactive-cloth-simulation',
    name: 'Cloth Physics',
    description: 'A simple verlet integration cloth simulation. Click and drag to interact with the fabric.',
    category: 'interactive',
    tags: ['cloth', 'physics', 'verlet', 'simulation', 'drag'],
    runtimeMode: 'p5',
    inputTypes: [],
    params: [
      { name: 'gridW', type: 'int', min: 8, max: 40, default: 20, label: 'Grid Width' },
      { name: 'gridH', type: 'int', min: 8, max: 30, default: 15, label: 'Grid Height' },
      { name: 'gravity', type: 'float', min: 0.05, max: 0.5, default: 0.15, label: 'Gravity' },
      { name: 'stiffness', type: 'int', min: 1, max: 10, default: 5, label: 'Stiffness' },
    ],
    code: `// @param int gridW 8 40 20
// @param int gridH 8 30 15
// @param float gravity 0.05 0.5 0.15
// @param int stiffness 1 10 5

let pts = [], links = [];
// Grid size is read once: changing it rebuilds the cloth on Restart.
const gw = genie.params.gridW ?? 20, gh = genie.params.gridH ?? 15;
function setup() {
  createCanvas(genie.width, genie.height);
  const spacing = width / (gw + 1);
  for (let y = 0; y < gh; y++) {
    for (let x = 0; x < gw; x++) {
      const px = (x + 1) * spacing, py = 40 + y * spacing;
      pts.push({ x: px, y: py, ox: px, oy: py, pinned: y === 0 });
    }
  }
  for (let y = 0; y < gh; y++) {
    for (let x = 0; x < gw; x++) {
      const i = y * gw + x;
      if (x < gw - 1) links.push({ a: i, b: i + 1, len: spacing });
      if (y < gh - 1) links.push({ a: i, b: i + gw, len: spacing });
    }
  }
}
function draw() {
  background(20);
  const gv = genie.params.gravity ?? 0.15;
  const iters = genie.params.stiffness ?? 5;
  // Mouse interaction
  if (mouseIsPressed) {
    for (const p of pts) {
      if (!p.pinned && dist(mouseX, mouseY, p.x, p.y) < 30) {
        p.x += (mouseX - pmouseX) * 0.5;
        p.y += (mouseY - pmouseY) * 0.5;
      }
    }
  }
  // Verlet integration
  for (const p of pts) {
    if (p.pinned) continue;
    const vx = p.x - p.ox, vy = p.y - p.oy;
    p.ox = p.x; p.oy = p.y;
    p.x += vx * 0.99; p.y += vy * 0.99 + gv;
    if (p.y > height - 5) { p.y = height - 5; p.oy = p.y; }
  }
  // Constraint solving
  for (let iter = 0; iter < iters; iter++) {
    for (const l of links) {
      const a = pts[l.a], b = pts[l.b];
      const dx = b.x - a.x, dy = b.y - a.y;
      const d = sqrt(dx * dx + dy * dy) || 0.01;
      const diff = (l.len - d) / d * 0.5;
      if (!a.pinned) { a.x -= dx * diff; a.y -= dy * diff; }
      if (!b.pinned) { b.x += dx * diff; b.y += dy * diff; }
    }
  }
  stroke(200, 220, 255, 120); strokeWeight(0.8); noFill();
  for (const l of links) {
    line(pts[l.a].x, pts[l.a].y, pts[l.b].x, pts[l.b].y);
  }
}`,
  },

  {
    id: 'interactive-paint-brush',
    name: 'Generative Paint Brush',
    description: 'A generative painting tool where mouse strokes create flowing, organic bristle marks with velocity-based width.',
    category: 'interactive',
    tags: ['paint', 'brush', 'drawing', 'generative', 'art'],
    runtimeMode: 'p5',
    inputTypes: [],
    params: [
      { name: 'bristleCount', type: 'int', min: 3, max: 30, default: 12, label: 'Bristles' },
      { name: 'spread', type: 'float', min: 5, max: 50, default: 20, label: 'Spread' },
      { name: 'opacity', type: 'float', min: 5, max: 60, default: 20, label: 'Opacity' },
      { name: 'hueRange', type: 'float', min: 10, max: 180, default: 40, label: 'Hue Range' },
    ],
    code: `// @param int bristleCount 3 30 12
// @param float spread 5 50 20
// @param float opacity 5 60 20
// @param float hueRange 10 180 40

let baseHue = 0;
function setup() {
  createCanvas(genie.width, genie.height);
  background(15);
  colorMode(HSB, 360, 100, 100, 100);
  noStroke();
}
function draw() {
  if (!mouseIsPressed) return;
  const bc = genie.params.bristleCount ?? 12;
  const sp = genie.params.spread ?? 20;
  const op = genie.params.opacity ?? 20;
  const hr = genie.params.hueRange ?? 40;
  const dx = mouseX - pmouseX, dy = mouseY - pmouseY;
  const vel = sqrt(dx * dx + dy * dy);
  if (vel < 0.5) return;
  const angle = atan2(dy, dx);
  const perpX = cos(angle + HALF_PI);
  const perpY = sin(angle + HALF_PI);
  baseHue = (baseHue + vel * 0.3) % 360;
  for (let i = 0; i < bc; i++) {
    const t = map(i, 0, bc - 1, -0.5, 0.5);
    const offset = t * sp;
    const jitter = random(-2, 2);
    const bx = mouseX + perpX * (offset + jitter);
    const by = mouseY + perpY * (offset + jitter);
    const pbx = pmouseX + perpX * (offset + jitter * 0.5);
    const pby = pmouseY + perpY * (offset + jitter * 0.5);
    const hue = (baseHue + t * hr) % 360;
    const size = map(vel, 0, 30, 4, 1);
    fill(hue, 60, 90, op);
    const steps = max(1, floor(vel / 3));
    for (let s = 0; s < steps; s++) {
      const st = s / steps;
      const sx = lerp(pbx, bx, st);
      const sy = lerp(pby, by, st);
      circle(sx, sy, size + random(1));
    }
  }
}`,
  },
];
